/**
 * WhereToBuyPicker — the ONE shared "Where to buy" store picker (T27.2.7.9.3),
 * used in the expanded item view of BOTH Planning (`ShoppingListItemRow`) and
 * Shopping (`CheckRow`). One implementation, not two diverging ones.
 *
 * The store anchor is the contract's single where-to-buy field
 * ([§ ShoppingListItem](../../../../docs/for-developers/ARCHITECTURE_CONTRACT.md#shoppinglistitem)):
 * the picker lists the household's SELECTED STORES and lets the household pick
 * one — deal or not. The deal is the OPTIONAL carried offer: when the chosen
 * store has a matching offer for the item, that offer rides along (so price /
 * FLYER still show). Re-tapping the chosen store clears BOTH back to "decide
 * later" — the carried deal can't outlive its store.
 *
 * The existing offer-matching (`matchOffersToItem`) only decides whether a deal
 * rides along with a store; it does NOT gate the list. A selected store with no
 * matching offer is still pickable (a plain store-only pick) — the common case
 * the old offer-gated picker couldn't serve.
 *
 * With ZERO selected stores the picker shows a short guide linking to store
 * settings (`routes.settingsStores`) — never an empty/dead list.
 *
 * Pure-presentational + token-only (CP5): it receives the resolved per-store
 * offers and the item's match context, computes the best matching offer per
 * store, and calls `onPick(storeLocationId | null, retailerOfferId | null)`.
 * The caller (PlanningModeView / ShoppingModeView) owns the orchestrator write.
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ShoppingListItemWithDetails } from '@shopista/shared/types';
import { routes } from '../../routes/paths';
import {
  brandQualifier,
  defaultStoreDeal,
  offerMatchesItem,
  pinnedStoreDeal,
  resolvedPrice,
  storeDealsForStore,
  type MatchedStoreLocationOffer,
  type StoreDealEntry,
  type StoreLocationOffers,
} from './storeLocationOfferResolver';
import * as s from './planningModeStyles';

/** Parse a bare ISO date (`YYYY-MM-DD`) as a local date, or null if unparseable. */
function parseIsoDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const [y, m, d] = value.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  const date = new Date(y, m - 1, d);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Whole-day difference (b - a), comparing local midnights so DST never skews it. */
function daysBetween(a: Date, b: Date): number {
  const aMid = new Date(a.getFullYear(), a.getMonth(), a.getDate()).getTime();
  const bMid = new Date(b.getFullYear(), b.getMonth(), b.getDate()).getTime();
  return Math.round((bMid - aMid) / 86_400_000);
}

/**
 * T27.2.7.9.5 (active-vs-upcoming) — compact validity for a per-store deal.
 *
 * An ACTIVE deal (a price you can get today) reads "ends <Mon D>" from `validTo`.
 * An UPCOMING deal (not yet started) reads "starts tomorrow" when `validFrom` is
 * exactly the day after `today`, else "starts <Mon D>" — so it never reads as a
 * current price. Both ISO dates (`YYYY-MM-DD`) are parsed as local dates (slicing
 * the day part so a bare date is never shifted across a timezone boundary).
 *
 * `today` is passed IN (not read via `new Date()` inline) so "tomorrow" is derived
 * from data and the helper stays unit-testable. Returns null when the relevant
 * date is missing or unparseable, so the caller renders no validity line.
 */
function formatDealValidity(
  status: 'active' | 'upcoming',
  validFrom: string | null | undefined,
  validTo: string | null | undefined,
  today: Date,
): string | null {
  if (status === 'upcoming') {
    const start = parseIsoDate(validFrom);
    if (!start) return null;
    if (daysBetween(today, start) === 1) return 'starts tomorrow';
    return `starts ${start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
  }
  const end = parseIsoDate(validTo);
  if (!end) return null;
  return `ends ${end.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
}

export interface WhereToBuyPickerProps {
  /** The list item being placed — `selected_storelocation_id` is the current pick. */
  item: ShoppingListItemWithDetails;
  /** The household's selected stores (id + name). The picker lists THESE. */
  selectedStores: { id: string; name: string | null }[];
  /**
   * The household's PREFERRED store id (the `is_favorite` store), pre-highlighted
   * when no pick is set yet. Null/undefined when none is marked. (`undefined`
   * permitted so a caller can thread an optional prop straight through under
   * `exactOptionalPropertyTypes`.)
   */
  preferredStoreId?: string | null | undefined;
  /** Resolved offers per store id (the always-mounted per-store loaders feed this). */
  storeLocationOffersById: Record<string, StoreLocationOffers>;
  /** True once the selected-store list query has resolved (vs still loading). */
  storesResolved: boolean;
  /** Liked brand NAMES (normalized) — for the "· your brand" qualifier. */
  likedBrandNames: ReadonlySet<string>;
  /** This item's HouseholdProduct's linked SystemProduct id(s) — identity match. */
  itemSystemProductIds: ReadonlySet<string>;
  /**
   * Persist a store pick that carries NO flyer pin. `(storeLocationId,
   * retailerOfferId)`:
   *   - pick a store with no matching flyer → `(storeId, offerId | null)`;
   *   - re-tap the chosen store (deselect)   → `(null, null)`.
   * A store WITH a default flyer auto-pins via `onPin` instead (T27.2.7.9.6), so a
   * price appears on select; this path is the no-flyer / deselect case. Either way
   * a bare `onPick` clears any existing pin (the pin can't outlive its store).
   */
  onPick: (storeLocationId: string | null, retailerOfferId: string | null) => void;
  /**
   * T27.2.7.9.6 — pin a flyer to the item (or clear the pin with `null`). The
   * deal is one of the store's matched `store_deals`; `retailerOfferId` is that
   * store's matched offer id (or null). The caller writes the store + offer + the
   * pin (`flyerdeal_id` + the validity snapshot) in ONE coherent write, so a
   * Walmart flyer can never apply to an Atlantic store. Used both for the
   * auto-pin-on-store-select (the store's default deal) and the per-deal pin / re-
   * pin controls. `onPick` is NOT fired separately — a bare `onPick` clears the
   * pin, which would wipe the pin this call is about to set (.9.6 reopen fix 2).
   */
  onPin: (deal: StoreDealEntry | null, retailerOfferId: string | null) => void;
  /**
   * T27.2.7.9.6 — "Buy regular here": clear the pinned flyer AND the carried
   * standard offer while KEEPING the selected store, so the compact row prices
   * nothing. Shown when an offer is pinned. Distinct from deselecting the store
   * (re-tapping the chosen store), which clears the store too.
   */
  onRemoveOffer: () => void;
  /**
   * T27.2.7.9.6 — re-open a pinned/listed flyer in the existing `DealOverlay`
   * (which self-loads from the `flyerdeal_id`). The caller mounts the overlay.
   */
  onOpenDeal: (flyerDealId: string) => void;
  /** Stable id prefix for testids (`plan-item-<id>` / `shop-item-<id>`). */
  testidPrefix: string;
}

/**
 * The best matching offer at a store, minus the deals-style stacked badges
 * (those belong only to the Deals surface, never the store picker). A local
 * lighter type so the shared `MatchedStoreLocationOffer` interface stays unchanged.
 */
type PickerStoreLocationOffer = Omit<MatchedStoreLocationOffer, 'badges'>;

/** The best matching offer at a store (preferred-brand-first, then cheapest), or null. */
function bestOfferForStore(
  item: ShoppingListItemWithDetails,
  store: StoreLocationOffers,
  likedBrandNames: ReadonlySet<string>,
  itemSystemProductIds: ReadonlySet<string>,
): PickerStoreLocationOffer | null {
  const matched: PickerStoreLocationOffer[] = [];
  for (const offer of store.offers) {
    if (!offerMatchesItem(item, offer, itemSystemProductIds)) continue;
    matched.push({
      storeId: store.storeId,
      storeName: store.storeName,
      offer,
      qualifier: brandQualifier(item, offer, likedBrandNames),
    });
  }
  if (matched.length === 0) return null;
  // Preferred → pick → substitute → unqualified; ties by lowest resolved price.
  const rank = (q: PickerStoreLocationOffer['qualifier']): number =>
    q === 'preferred' ? 0 : q === 'pick' ? 1 : q === 'substitute' ? 2 : 3;
  matched.sort((a, b) => {
    const byRank = rank(a.qualifier) - rank(b.qualifier);
    if (byRank !== 0) return byRank;
    const ap = resolvedPrice(a.offer);
    const bp = resolvedPrice(b.offer);
    if (ap == null) return 1;
    if (bp == null) return -1;
    return ap - bp;
  });
  return matched[0] ?? null;
}

export function WhereToBuyPicker({
  item,
  selectedStores,
  preferredStoreId,
  storeLocationOffersById,
  storesResolved,
  likedBrandNames,
  itemSystemProductIds,
  onPick,
  onPin,
  onRemoveOffer,
  onOpenDeal,
  testidPrefix,
}: WhereToBuyPickerProps): JSX.Element {
  const navigate = useNavigate();

  // A single reference "today" for every row's validity label (so "starts
  // tomorrow" derives from data, not a per-call `new Date()`). Memoized once per
  // render — re-evaluated only if the item identity changes.
  const today = useMemo(() => new Date(), [item.id]);

  const pickedStoreId = item.selected_storelocation_id ?? null;

  // One row per selected store, each carrying its best matching offer (or null).
  // The store list is driven by the household's selection — NOT by which stores
  // have offers (the offer-gating bug this task fixes).
  const rows = useMemo(
    () =>
      selectedStores.map((store) => {
        const resolved = storeLocationOffersById[store.id];
        const best =
          resolved && !resolved.loading
            ? bestOfferForStore(item, resolved, likedBrandNames, itemSystemProductIds)
            : null;
        return { store, best };
      }),
    [selectedStores, storeLocationOffersById, item, likedBrandNames, itemSystemProductIds],
  );

  // No selected stores → the guide to store settings (never an empty list).
  if (selectedStores.length === 0) {
    return (
      <div style={s.wherePickStyle} data-testid={`${testidPrefix}-picker`}>
        <div
          style={s.forthcomingStyle}
          data-testid={`${testidPrefix}-no-stores`}
        >
          Pick your stores to choose where to buy this.
        </div>
        <button
          type="button"
          style={s.moreBrandsStyle}
          onClick={() => navigate(routes.settingsStores)}
          data-testid={`${testidPrefix}-pick-stores`}
        >
          Choose your stores ›
        </button>
      </div>
    );
  }

  return (
    <div style={s.wherePickStyle} data-testid={`${testidPrefix}-picker`}>
      {rows.map(({ store, best }) => {
        const isSel = store.id === pickedStoreId;
        // Pre-highlight the preferred store only while nothing is picked yet.
        const isPreferred =
          pickedStoreId == null && preferredStoreId === store.id;
        const offer = best?.offer ?? null;
        // T27.2.7.9.6 — the FULL set of live Flyer deals at this store (the real
        // on-sale prices), read from the item's `store_deals` and DECOUPLED from
        // the catalog offer: a store with live deals but NO `retaileroffers` row
        // (so `best`/`offer` is null) still shows them and stays selectable.
        const storeDeals = storeDealsForStore(item, store.id);
        // The store's default deal (cheapest-active flyer, else cheapest upcoming).
        // Drives the auto-pin-on-select (the unpinned store-row no longer shows a
        // summary price — prices live on the browse rows near the flyer).
        const defaultDeal = defaultStoreDeal(item, store.id);
        // T27.2.7.9.6 (reopen) — the store row shows a price ONLY when this store is
        // the SELECTED store AND it carries the item's PINNED flyer; the cell then
        // summarizes that pinned deal. For every other store — unselected, or
        // selected-with-no-pin — the store row shows NO price; the per-store prices
        // live on the browse rows below (near the flyer). User decision: "the price
        // near the store is shown only when a flyer is pinned; no pinned flyers →
        // price is shown only near the flyer, not the store."
        const pinnedAtStore = pinnedStoreDeal(item, store.id);
        const storeRowDeal = isSel ? pinnedAtStore : null;
        // T27.2.7.9.6 — render EVERY store with ≥1 matched deal as a browse list
        // (each deal labelled by flyer title, with a pin/re-pin toggle + View), so a
        // single-deal store's flyer is pinnable, removable, and viewable too — not
        // only browsable when several exist. (Supersedes .9.6 reopen fix 3's
        // View-only sibling: the browse row carries both pin and View.)
        const hasDeals = storeDeals.length > 0;
        return (
          <div key={store.id} data-testid={`${testidPrefix}-store-${store.id}-group`}>
            <div style={s.storeRowWrapStyle}>
              <button
                type="button"
                style={s.storeRowStyle}
                onClick={() => {
                  // T27.2.7.9.6 — re-tap the chosen store → deselect it (clear store
                  // + pin). Else SELECT: auto-pin the store's default deal when it
                  // has one (store + offer + pin in ONE write via `onPin`, so a
                  // price appears on select), else pick the store with no flyer pin.
                  // A bare `onPick` clears any prior pin (the pin can't outlive its
                  // store — a Walmart flyer can't apply to an Atlantic pick).
                  if (isSel) {
                    onPick(null, null);
                  } else if (defaultDeal) {
                    onPin(defaultDeal, offer?.id ?? null);
                  } else {
                    onPick(store.id, offer?.id ?? null);
                  }
                }}
                aria-pressed={isSel}
                data-testid={`${testidPrefix}-store-${store.id}`}
              >
                <span
                  style={isSel ? s.storeRadioSelStyle : s.storeRadioStyle}
                  aria-hidden="true"
                />
                <span style={s.storeNameStyle}>
                  {store.name ?? 'Store'}
                  {isPreferred && (
                    <span
                      style={s.qualifierStyle}
                      data-testid={`${testidPrefix}-store-${store.id}-preferred`}
                    >
                      {' '}
                      · preferred
                    </span>
                  )}
                  {best && (
                    <span style={s.storeMatchLineStyle}>
                      {best.offer.title}
                      {best.qualifier === 'preferred' && (
                        <span style={s.qualifierStyle}> · your brand</span>
                      )}
                      {best.qualifier === 'pick' && (
                        <span style={s.pickQualifierStyle}> · your pick</span>
                      )}
                      {best.qualifier === 'substitute' && (
                        <span style={s.subQualifierStyle}> · substitute</span>
                      )}
                    </span>
                  )}
                </span>
                {/* The store-row cell shows a price ONLY for the SELECTED store
                    carrying the PINNED flyer (`storeRowDeal`); otherwise it renders
                    nothing — the per-store prices live on the browse rows below,
                    near the flyer. The per-deal browse rows carry the pin/re-pin +
                    View. */}
                <StoreDealCell
                  deal={storeRowDeal}
                  today={today}
                  testid={`${testidPrefix}-store-${store.id}`}
                />
              </button>
            </div>

            {/* T27.2.7.9.6 — the browse list: ANY store with ≥1 matched deal lists
                its deals, each labelled by flyer title, with a pin / re-pin toggle
                and a View. A single-deal store renders here too (one row), so its
                flyer is pinnable, removable, and viewable — not only via the store
                row's auto-pin. */}
            {hasDeals && (
              <div
                style={s.dealBrowseListStyle}
                data-testid={`${testidPrefix}-store-${store.id}-deals`}
              >
                {storeDeals.map((deal, index) => {
                  const isPinned = item.flyerdeal_id === deal.flyerdeal_id;
                  // Render the divider only BETWEEN browse rows — never under the
                  // last (a single-deal store has no divider at all; a multi-deal
                  // store has no trailing line under its final row).
                  const isLastRow = index === storeDeals.length - 1;
                  return (
                    <div
                      key={deal.flyerdeal_id}
                      style={isLastRow ? s.dealBrowseRowLastStyle : s.dealBrowseRowStyle}
                      data-testid={`${testidPrefix}-deal-${deal.flyerdeal_id}`}
                    >
                      <button
                        type="button"
                        style={s.dealPinButtonStyle}
                        // Pin the flyer AND set its store + offer in ONE coherent
                        // write (`onPin` carries the store's matched offer id) — so a
                        // flyer always belongs to its store. We do NOT fire a
                        // separate `onPick` here: a bare `onPick` now clears the pin
                        // (.9.6 reopen fix 2), which would wipe the pin we're setting.
                        // Re-tap the pinned deal → unpin (back to no pinned price),
                        // keeping the store.
                        onClick={() =>
                          onPin(isPinned ? null : deal, offer?.id ?? null)
                        }
                        aria-pressed={isPinned}
                        data-testid={`${testidPrefix}-deal-${deal.flyerdeal_id}-pin`}
                      >
                        <svg
                          viewBox="0 0 16 16"
                          fill="currentColor"
                          aria-hidden="true"
                          style={isPinned ? s.dealPinIconActiveStyle : s.dealPinIconStyle}
                        >
                          <path d="M9.828.722a.5.5 0 0 1 .354.146l4.95 4.95a.5.5 0 0 1 0 .707c-.48.48-1.072.588-1.503.588-.177 0-.335-.018-.46-.039l-3.134 3.134a6 6 0 0 1 .16 1.013c.046.702-.032 1.687-.72 2.375a.5.5 0 0 1-.707 0l-2.829-2.828-3.182 3.182c-.195.195-1.219.902-1.414.707s.512-1.22.707-1.414l3.182-3.182-2.828-2.829a.5.5 0 0 1 0-.707c.688-.688 1.673-.766 2.375-.72a6 6 0 0 1 1.013.16l3.134-3.133a3 3 0 0 1-.04-.461c0-.43.108-1.022.589-1.503a.5.5 0 0 1 .353-.146" />
                        </svg>
                        <span style={s.dealBrowseTitleStyle}>{deal.title}</span>
                        <StoreDealCell
                          deal={deal}
                          today={today}
                          testid={`${testidPrefix}-deal-${deal.flyerdeal_id}`}
                        />
                      </button>
                      {/* Re-open this flyer in the shared deal overlay (it
                          self-loads from the `flyerdeal_id`). A sibling control —
                          a nested button inside the pin button is invalid. */}
                      <button
                        type="button"
                        style={s.dealViewButtonStyle}
                        onClick={() => onOpenDeal(deal.flyerdeal_id)}
                        aria-label={`View the ${deal.title} flyer deal`}
                        data-testid={`${testidPrefix}-deal-${deal.flyerdeal_id}-view`}
                      >
                        View
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
      {/* T27.2.7.9.6 — "Buy regular here": shown when a flyer is pinned. Clears the
          pinned flyer + the carried offer while KEEPING the selected store, so the
          compact row prices nothing — buy the item at the chosen store at its
          regular price. Distinct from re-tapping the store (which deselects it). */}
      {item.flyerdeal_id != null && (
        <div style={s.buyRegularRowStyle}>
          <button
            type="button"
            style={s.buyRegularButtonStyle}
            onClick={() => onRemoveOffer()}
            data-testid={`${testidPrefix}-buy-regular`}
          >
            Buy regular here
          </button>
        </div>
      )}
      <div
        style={pickedStoreId ? s.pickedLineActiveStyle : s.pickedLineStyle}
        data-testid={`${testidPrefix}-picked`}
      >
        {pickedStoreId
          ? 'On your trip — tap again to remove.'
          : "Decide later — we'll suggest the best stop."}
      </div>
      {/* While a selected store's offers are still resolving, a calm note so the
          list never reads as "no deals" prematurely. */}
      {!storesResolved && (
        <div style={s.forthcomingStyle} data-testid={`${testidPrefix}-loading`}>
          Checking your stores for deals…
        </div>
      )}
    </div>
  );
}

/**
 * T27.2.7.9.5/.9.6 — the deal price + FLYER + validity cell. Reused by the store
 * row (the effective pinned-or-default deal) and each browse row (one matched
 * deal). `null` deal → renders nothing (the store has no live deal). An UPCOMING
 * deal renders in the distinct lower-emphasis style and reads "starts <date>",
 * never as a current price; an ACTIVE deal keeps the on-sale style ("ends <date>").
 * The price/validity testids are derived from `testid` so a one-deal store keeps
 * the .9.5 ids (`…-store-<id>-deal-price`) while a browse row gets its own
 * (`…-deal-<flyerDealId>-deal-price`).
 */
function StoreDealCell({
  deal,
  today,
  testid,
}: {
  deal: StoreDealEntry | null;
  today: Date;
  testid: string;
}): JSX.Element | null {
  if (!deal) return null;
  const isUpcoming = deal.status === 'upcoming';
  const validity = formatDealValidity(
    deal.status,
    deal.valid_from,
    deal.valid_to,
    today,
  );
  return (
    <span style={s.storeDealCellStyle}>
      <span style={s.storeDealPriceRowStyle}>
        <span
          style={isUpcoming ? s.upcomingPriceStyle : s.priceStyle}
          data-testid={`${testid}-deal-price`}
        >
          ${deal.sale_price.toFixed(2)}
        </span>
        {/* FLYER rides on the live store deal, NOT a catalog offer. An upcoming
            deal uses a distinct, secondary FLYER treatment so it never reads as
            the active on-sale badge. */}
        <span style={isUpcoming ? s.upcomingFlyerBadgeStyle : s.flyerBadgeStyle}>
          FLYER
        </span>
      </span>
      {validity != null && (
        <span
          style={isUpcoming ? s.upcomingValidityStyle : s.dealValidityStyle}
          data-testid={`${testid}-deal-validity`}
        >
          {validity}
        </span>
      )}
    </span>
  );
}
